import React, { useMemo } from 'react'
import { View, StyleSheet } from 'react-native'
import { Icon, Button, OverflowMenu, MenuItem, Text } from '@ui-kitten/components'
import { useTranslation } from 'react-i18next'
import { getProductPrice } from '../utils/priceTypeHelpers'
import { useColors } from '@/utils/useColors'

/**
 * Component for selecting price type during a sale
 * @param {Object} selectedPriceType - Currently selected price type
 * @param {Function} onSelect - Callback when price type is selected
 * @param {Array} priceTypes - Available price types
 * @param {Array} cartItems - Current cart items
 * @param {string} store - Current store ID
 * @param {boolean} disabled - Whether the selector is disabled
 * @param {Object} style - Additional styles
 */
export const PriceTypeSelect = React.memo(function PriceTypeSelect({
  selectedPriceType,
  onSelect,
  priceTypes = [],
  cartItems = [],
  store,
  disabled = false,
  style
}) {
  const { t } = useTranslation()
  const colors = useColors()
  const [menuVisible, setMenuVisible] = React.useState(false)

  // Add default price type and filter available options
  const availablePriceTypes = useMemo(() => {
    const defaultPriceType = {
      id: null,
      name: t('newSale.defaultPrice') || 'Default Price',
      icon: 'pricetags-outline',
      isDefault: true
    }
    
    if (!cartItems || cartItems.length === 0) {
      return [defaultPriceType, ...priceTypes]
    }

    // Filter additional price types to only show those with prices set
    const additionalTypes = priceTypes.filter(priceType => {
      return cartItems.some(item => {
        const price = getProductPrice(item, store, priceType.id)
        return price && price > 0
      })
    })
    
    return [defaultPriceType, ...additionalTypes]
  }, [priceTypes, cartItems, store, t])

  const selectedIndex = useMemo(() => {
    if (!selectedPriceType) return null
    const index = availablePriceTypes.findIndex(pt => pt.id === selectedPriceType.id)
    return index >= 0 ? { row: index } : null
  }, [selectedPriceType, availablePriceTypes])

  // If no additional price types configured, don't show selector
  if (!priceTypes || priceTypes.length === 0) {
    return null
  }

  const renderToggleButton = () => {
    const isDefault = !selectedPriceType || selectedPriceType.id === null

    // UI Kitten Button strips testID from the rendered DOM node, so wrap it in a
    // View that carries the testID for reliable e2e targeting (price-type-select-button).
    return (
      <View testID='price-type-select-button'>
        <Button
          appearance='outline'
          status={isDefault ? 'basic' : 'primary'}
          style={[styles.button, style]}
          size='small'
          disabled={disabled}
          accessoryLeft={(props) => (
            <Icon
              {...props}
              name={selectedPriceType?.icon || 'pricetags-outline'}
              style={{...props.style, marginRight: isDefault ? 8 : -5}}
            />
          )}
          onPress={() => setMenuVisible(true)}
        >
          {isDefault ? '' : selectedPriceType?.name}
        </Button>
      </View>
    )
  }

  return (
    <OverflowMenu
      anchor={renderToggleButton}
      visible={menuVisible}
      selectedIndex={selectedIndex}
      onSelect={(index) => {
        const selected = availablePriceTypes[index.row]
        if (selected) {
          onSelect(selected)
        }
        setMenuVisible(false)
      }}
      onBackdropPress={() => setMenuVisible(false)}
    >
      {availablePriceTypes.map((priceType, index) => (
        <MenuItem
          key={priceType.id}
          testID={`price-type-option-${priceType.id ?? 'default'}`}
          title={priceType.name}
          accessoryLeft={(props) => (
          <Icon
            {...props}
            name={priceType.icon || 'pricetags-outline'}
          />
          )}
        />
      ))}
    </OverflowMenu>
  )
}, (prevProps, nextProps) => {
  // Memoization check
  if (prevProps.selectedPriceType?.id !== nextProps.selectedPriceType?.id) {
    return false
  }
  if (prevProps.disabled !== nextProps.disabled) {
    return false
  }
  if (prevProps.priceTypes?.length !== nextProps.priceTypes?.length) {
    return false
  }
  if (prevProps.store !== nextProps.store) {
    return false
  }
  if (prevProps.cartItems?.length !== nextProps.cartItems?.length) {
    return false
  }
  return true
})

const styles = StyleSheet.create({
  button: {
    paddingHorizontal: 0
  }
})

